import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Field } from "@base-ui/react/field";
import { Combobox } from "@base-ui/react/combobox";
import {
  DismissIcon,
  ExpandIcon,
  TickIcon,
} from "../../primitives/Icons/Icons";
import Spinner from "../../primitives/Spinner/Spinner";
import css from "./Autocomplete.module.css";

const labelOf = (itemToStringLabel) => (item) =>
  itemToStringLabel ? itemToStringLabel(item) : item?.label ?? String(item);

function AutocompleteChip({ label }) {
  return (
    <Combobox.Chip className={css.chip}>
      {label}
      <Combobox.ChipRemove
        className={css.chipRemove}
        aria-label={`Remove ${label}`}
      >
        <DismissIcon />
      </Combobox.ChipRemove>
    </Combobox.Chip>
  );
}

AutocompleteChip.propTypes = { label: PropTypes.string.isRequired };

const AutocompleteRoot = React.forwardRef(
  (
    {
      bare = false,
      value,
      defaultValue,
      onValueChange,
      onInputValueChange,
      itemToStringLabel,
      multiple = false,
      disabled = false,
      required = false,
      readOnly = false,
      name,
      label,
      description,
      error,
      placeholder,
      leading,
      loading = false,
      clearable = true,
      autoHighlight = true,
      open,
      onOpenChange,
      className,
      children,
      status,
      inputProps = {},
      ...rest
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    const comboboxContent = (
      <Combobox.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        onInputValueChange={onInputValueChange}
        itemToStringLabel={itemToStringLabel}
        multiple={multiple}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        name={name}
        autoHighlight={autoHighlight}
        open={open}
        onOpenChange={onOpenChange}
        {...rest}
      >
        <Combobox.InputGroup className={css.inputGroup}>
          {multiple && (
            <Combobox.Chips className={css.chips}>
              <Combobox.Value>
                {(chosen) =>
                  chosen
                    .map(labelOf(itemToStringLabel))
                    .map((label) => (
                      <AutocompleteChip key={label} label={label} />
                    ))
                }
              </Combobox.Value>
            </Combobox.Chips>
          )}
          {leading && <span className={css.leading}>{leading}</span>}
          <Combobox.Input
            ref={ref}
            className={css.input}
            placeholder={placeholder}
            {...inputProps}
          />
          {loading && <Spinner size="sm" className={css.spinner} />}
          {clearable && (
            <Combobox.Clear
              className={css.clear}
              aria-label="Clear"
              disabled={disabled}
            >
              <DismissIcon />
            </Combobox.Clear>
          )}
          <Combobox.Trigger className={css.trigger}>
            <ExpandIcon className={css.chevron} />
          </Combobox.Trigger>
        </Combobox.InputGroup>
        <Combobox.Portal>
          <Combobox.Positioner
            className={css.positioner}
            sideOffset={4}
            align="start"
          >
            <Combobox.Popup className={css.popup}>
              <Combobox.Status className={css.status}>{status}</Combobox.Status>
              <Combobox.List className={css.list}>{children}</Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    );

    if (bare) {
      return comboboxContent;
    }

    return (
      <Field.Root
        className={clsx(css.root, className)}
        disabled={disabled}
        invalid={hasError}
      >
        {label && <Field.Label className={css.label}>{label}</Field.Label>}
        {comboboxContent}
        {description && !hasError && (
          <Field.Description className={css.description}>
            {description}
          </Field.Description>
        )}
        {typeof error === "string" && (
          <Field.Error className={css.error} match>
            {error}
          </Field.Error>
        )}
      </Field.Root>
    );
  },
);

AutocompleteRoot.displayName = "AutocompleteRoot";

AutocompleteRoot.propTypes = {
  bare: PropTypes.bool,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onValueChange: PropTypes.func,
  /* Control the text as well as the selection — needed to read what was typed
     but never selected. Pairs with onInputValueChange. */
  inputValue: PropTypes.string,
  onInputValueChange: PropTypes.func,
  /* Combobox.Root forwards this to its hidden form input as a browser autofill
     hint; it does not change filtering, which Base UI applies only to `items`. */
  autoComplete: PropTypes.string,
  itemToStringLabel: PropTypes.func,
  /* Reaches the underlying <input> for attributes the wrapper does not model —
     maxLength, onBlur, autoFocus. */
  inputProps: PropTypes.object,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
  leading: PropTypes.node,
  loading: PropTypes.bool,
  /* Base UI mounts the clear button only when there is a value to clear, so it
     costs nothing when empty. Opt out for fields that must always hold one. */
  clearable: PropTypes.bool,
  autoHighlight: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /* Mounted even when empty: a live region that appears with its text is not reliably announced. */
  status: PropTypes.node,
};

const AutocompleteItem = React.forwardRef(
  ({ value, disabled = false, className, children, ...rest }, ref) => (
    <Combobox.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={clsx(css.item, className)}
      {...rest}
    >
      <span className={css.itemIndicator}>
        <Combobox.ItemIndicator>
          <TickIcon />
        </Combobox.ItemIndicator>
      </span>
      {children}
    </Combobox.Item>
  ),
);

AutocompleteItem.displayName = "AutocompleteItem";

AutocompleteItem.propTypes = {
  value: PropTypes.any.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const AutocompleteGroup = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <Combobox.Group ref={ref} className={clsx(css.group, className)} {...rest}>
      {children}
    </Combobox.Group>
  ),
);

AutocompleteGroup.displayName = "AutocompleteGroup";

AutocompleteGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AutocompleteGroupLabel = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <Combobox.GroupLabel
      ref={ref}
      className={clsx(css.groupLabel, className)}
      {...rest}
    >
      {children}
    </Combobox.GroupLabel>
  ),
);

AutocompleteGroupLabel.displayName = "AutocompleteGroupLabel";

AutocompleteGroupLabel.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AutocompleteEmpty = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <Combobox.Empty ref={ref} className={clsx(css.empty, className)} {...rest}>
      {children}
    </Combobox.Empty>
  ),
);

AutocompleteEmpty.displayName = "AutocompleteEmpty";

AutocompleteEmpty.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Autocomplete = {
  Root: AutocompleteRoot,
  Item: AutocompleteItem,
  Group: AutocompleteGroup,
  GroupLabel: AutocompleteGroupLabel,
  Empty: AutocompleteEmpty,
};

export default Autocomplete;
